import type { JSX } from 'react';

import type {
  RemainingEffortReport,
  RestaufwandArbeitspaket,
  RestaufwandProjekt,
} from '@asas/domain';
import type { IsoWeek } from '@asas/domain/iso-week';

import type { FehlerBefund } from '../daten/fehler';

import {
  stundenAnzeige,
  wocheAnzeige,
  wocheAusWert,
  wocheWert,
  zeitpunktAnzeige,
} from './restaufwand';
import type { MeldeEingabe, MeldeHinweise } from './restaufwand';

/**
 * Die Ansichten zum Restaufwand (AC-601).
 *
 * Reine Ansichten: Daten und Rueckrufe hinein, Markup hinaus. Zustand, Netz und
 * Fehlerbehandlung liegen an EINER Stelle (`RestaufwandBereich`). Bewusst
 * schmucklos; die Portierung des Mockups (E2 ff.) ersetzt die Darstellung.
 *
 * Der Weg zur Eingabe hat drei Klicks (AK 4, E10): „Restaufwand melden" →
 * Projekt → Arbeitspaket. Der Restaufwand ist eine Steuerungsgroesse, kein
 * Leistungsmass (Kapitel 8.2): nichts hier sortiert oder summiert nach Person.
 */

export interface RestaufwandKopfzeileProps {
  readonly geladen: boolean;
  readonly laedt: boolean;
  readonly ladefehler: FehlerBefund | null;
  /** Nach einem 403 aus einer der vier Prozeduren: bis zum Neuladen der Seite kein Versuch mehr. */
  readonly zugriffGesperrt: boolean;
  readonly anmeldeUrl: string;
  readonly onOeffnen: () => void;
}

/** Klick 1. Ein nicht wiederholbarer Befund (403) sperrt das Laden bis zum Neuladen der Seite. */
export function RestaufwandKopfzeile({
  geladen,
  laedt,
  ladefehler,
  zugriffGesperrt,
  anmeldeUrl,
  onOeffnen,
}: RestaufwandKopfzeileProps): JSX.Element {
  const gesperrt = laedt || zugriffGesperrt || (ladefehler !== null && !ladefehler.wiederholbar);

  return (
    <>
      {ladefehler === null ? null : <p role="alert">{ladefehler.meldung}</p>}
      {ladefehler?.neuAnmelden === true ? (
        <p>
          <a href={anmeldeUrl}>Neu anmelden</a>
        </p>
      ) : null}
      <p>
        <button type="button" disabled={gesperrt} onClick={onOeffnen}>
          {laedt ? 'Wird geladen …' : geladen ? 'Projekte neu laden' : 'Restaufwand melden'}
        </button>
      </p>
    </>
  );
}

export interface ProjektAuswahlProps {
  readonly projekte: readonly RestaufwandProjekt[];
  readonly gesamt: number;
  /** `remaining_effort:report_any`: dann gibt es schlicht keine beauftragten Projekte. */
  readonly mandantenweit?: boolean;
  readonly suche: string;
  readonly gewaehlt: string | null;
  readonly gesperrt: boolean;
  readonly onSuche: (text: string) => void;
  readonly onWaehlen: (projekt: RestaufwandProjekt) => void;
}

/** Klick 2. Tippen in die Suche ist kein Klick. */
export function ProjektAuswahl({
  projekte,
  gesamt,
  mandantenweit = false,
  suche,
  gewaehlt,
  gesperrt,
  onSuche,
  onWaehlen,
}: ProjektAuswahlProps): JSX.Element {
  // AC-107-1 (E4): ohne `remaining_effort:report_any` nur Projekte mit eigener
  // freigegebener Belegung — die Leermeldung sagt, warum die Liste leer ist.
  if (gesamt === 0 && mandantenweit) return <p>Keine beauftragten Projekte.</p>;
  if (gesamt === 0) {
    return (
      <p>
        Keine Projekte zum Melden. Gemeldet wird auf beauftragten Projekten, auf denen Sie in den
        letzten 26 oder den nächsten 8 Wochen freigegeben eingeplant sind.
      </p>
    );
  }

  return (
    <>
      <p>
        <label>
          Projekt suchen{' '}
          <input
            type="search"
            value={suche}
            onChange={(event) => {
              onSuche(event.target.value);
            }}
          />
        </label>
      </p>
      <ul aria-label="Projekte">
        {projekte.map((projekt) => (
          <li key={projekt.id}>
            <button
              type="button"
              disabled={gesperrt}
              aria-pressed={projekt.id === gewaehlt}
              onClick={() => {
                onWaehlen(projekt);
              }}
            >
              {projekt.number} {projekt.name}
            </button>
            {projekt.meldbar ? null : <> (nur lesen)</>}
          </li>
        ))}
      </ul>
      {projekte.length === 0 ? <p>Kein Projekt passt zur Suche.</p> : null}
    </>
  );
}

export interface ArbeitspaketListeProps {
  readonly projekt: RestaufwandProjekt;
  readonly pakete: readonly RestaufwandArbeitspaket[];
  readonly gewaehlt: string | null;
  readonly gesperrt: boolean;
  readonly onWaehlen: (paket: RestaufwandArbeitspaket) => void;
}

/** Klick 3: das Arbeitspaket waehlen; Formular und Historie oeffnen sich darunter. */
export function ArbeitspaketListe({
  projekt,
  pakete,
  gewaehlt,
  gesperrt,
  onWaehlen,
}: ArbeitspaketListeProps): JSX.Element {
  if (pakete.length === 0) return <p>Das Projekt hat noch keine Arbeitspakete.</p>;

  return (
    <table>
      <caption>
        Arbeitspakete · {projekt.number} {projekt.name}
      </caption>
      <thead>
        <tr>
          <th scope="col">Arbeitspaket</th>
          <th scope="col">Plan</th>
          <th scope="col">Restaufwand</th>
          <th scope="col">Fertig</th>
          <th scope="col">Stand</th>
          <th scope="col">Aktion</th>
        </tr>
      </thead>
      <tbody>
        {pakete.map((paket) => (
          <tr key={paket.id} aria-current={paket.id === gewaehlt ? 'true' : undefined}>
            <td>{paket.name}</td>
            <td>
              {paket.planHours === null ? 'noch nicht geplant' : stundenAnzeige(paket.planHours)}
            </td>
            <td>
              {paket.stand === null ? '—' : stundenAnzeige(paket.stand.restHours)}
              {paket.stand?.drift === true ? <strong> Drift</strong> : null}
              {paket.stand?.widerspruch === true ? <strong> Widerspruch</strong> : null}
            </td>
            <td>{paket.stand === null ? '—' : `${String(paket.stand.fertigstellungsgrad)} %`}</td>
            <td>{paket.stand === null ? '—' : wocheAnzeige(paket.stand.bezugswoche)}</td>
            <td>
              <button
                type="button"
                disabled={gesperrt}
                onClick={() => {
                  onWaehlen(paket);
                }}
              >
                {projekt.meldbar ? `${paket.name} melden` : `${paket.name} ansehen`}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export interface MeldeFormularProps {
  readonly paket: RestaufwandArbeitspaket;
  readonly eingabe: MeldeEingabe;
  readonly hinweise: MeldeHinweise;
  readonly wochen: readonly IsoWeek[];
  readonly sendet: boolean;
  readonly gesperrt: boolean;
  /** Rueckfrage bei 100 % mit Restaufwand offen (AK 5). */
  readonly rueckfrage: boolean;
  readonly onAendern: (eingabe: MeldeEingabe) => void;
  readonly onSpeichern: () => void;
  readonly onBestaetigen: () => void;
  readonly onRueckfrageAbbrechen: () => void;
}

export function MeldeFormular({
  paket,
  eingabe,
  hinweise,
  wochen,
  sendet,
  gesperrt,
  rueckfrage,
  onAendern,
  onSpeichern,
  onBestaetigen,
  onRueckfrageAbbrechen,
}: MeldeFormularProps): JSX.Element {
  const aus = sendet || gesperrt;
  const unvollstaendig =
    hinweise.stunden === null ||
    hinweise.grad === null ||
    hinweise.kommentarZuLang ||
    (hinweise.kommentarPflicht && eingabe.kommentar.trim() === '');

  return (
    <form
      aria-label={`Restaufwand melden: ${paket.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        onSpeichern();
      }}
    >
      <p>
        <label>
          Restaufwand in Stunden{' '}
          <input
            inputMode="decimal"
            value={eingabe.restHours}
            disabled={aus}
            aria-invalid={eingabe.restHours !== '' && hinweise.stunden === null}
            onChange={(event) => {
              onAendern({ ...eingabe, restHours: event.target.value });
            }}
          />
        </label>{' '}
        <label>
          Fertigstellungsgrad in %{' '}
          <input
            inputMode="numeric"
            value={eingabe.fertigstellungsgrad}
            disabled={aus}
            aria-invalid={eingabe.fertigstellungsgrad !== '' && hinweise.grad === null}
            onChange={(event) => {
              onAendern({ ...eingabe, fertigstellungsgrad: event.target.value });
            }}
          />
        </label>{' '}
        <label>
          Bezugswoche{' '}
          <select
            value={wocheWert(eingabe.bezugswoche)}
            disabled={aus}
            onChange={(event) => {
              const woche = wocheAusWert(event.target.value, wochen);
              if (woche !== null) onAendern({ ...eingabe, bezugswoche: woche });
            }}
          >
            {wochen.map((woche, index) => (
              <option key={wocheWert(woche)} value={wocheWert(woche)}>
                {wocheAnzeige(woche)}
                {index === 0 ? ' (laufende Woche)' : ''}
              </option>
            ))}
          </select>
        </label>
      </p>
      {eingabe.restHours !== '' && hinweise.stunden === null ? (
        <p>Stunden als Zahl ab 0, höchstens zwei Nachkommastellen.</p>
      ) : null}
      {eingabe.fertigstellungsgrad !== '' && hinweise.grad === null ? (
        <p>Fertigstellungsgrad als ganze Zahl von 0 bis 100.</p>
      ) : null}
      <p>
        <label>
          Kommentar{hinweise.kommentarPflicht ? ' (Pflicht bei Erhöhung)' : ''}{' '}
          <textarea
            value={eingabe.kommentar}
            disabled={aus}
            required={hinweise.kommentarPflicht}
            onChange={(event) => {
              onAendern({ ...eingabe, kommentar: event.target.value });
            }}
          />
        </label>
      </p>
      {hinweise.kommentarPflicht ? (
        <p>Höher als {stundenAnzeige(hinweise.vergleich)} — bitte begründen Sie die Erhöhung.</p>
      ) : null}
      {hinweise.kommentarZuLang ? <p>Der Kommentar ist zu lang (höchstens 1000 Zeichen).</p> : null}
      {hinweise.drift ? (
        <p>
          <strong>Drift:</strong> Der Restaufwand liegt über dem Plan von{' '}
          {stundenAnzeige(paket.planHours)}. Die Meldung ist erlaubt und wird als Drift markiert.
        </p>
      ) : null}
      {rueckfrage ? (
        <div role="alertdialog" aria-label="Rückfrage">
          <p>
            Sie melden 100 % Fertigstellung, aber noch {stundenAnzeige(hinweise.stunden)}{' '}
            Restaufwand. Trotzdem speichern?
          </p>
          <button type="button" disabled={aus} onClick={onBestaetigen}>
            Ja, trotzdem speichern
          </button>{' '}
          <button type="button" disabled={sendet} onClick={onRueckfrageAbbrechen}>
            Zurück zur Eingabe
          </button>
        </div>
      ) : (
        <p>
          <button type="submit" disabled={aus || unvollstaendig}>
            {sendet ? 'Wird gespeichert …' : 'Meldung speichern'}
          </button>
        </p>
      )}
    </form>
  );
}

export interface HistorieProps {
  readonly paket: RestaufwandArbeitspaket;
  readonly meldungen: readonly RemainingEffortReport[] | null;
  readonly laedt: boolean;
}

/**
 * Alle Meldungen EINES Arbeitspakets, neueste zuerst (AK 2). Der Urheber steht
 * hier und nur hier (E11); es gibt keine Ansicht „Meldungen von Person X".
 */
export function Historie({ paket, meldungen, laedt }: HistorieProps): JSX.Element {
  if (meldungen === null) return <p>{laedt ? 'Historie wird geladen …' : ''}</p>;
  if (meldungen.length === 0) return <p>Noch keine Meldung zu {paket.name}.</p>;

  return (
    <>
      {meldungen.some((meldung) => meldung.widerspruch) ? (
        <p>
          <strong>Widerspruch:</strong> Für mindestens eine Woche haben zwei Konten unterschiedliche
          Werte gemeldet. Es gilt die jüngste Meldung; bitte stimmen Sie sich ab und melden Sie den
          gemeinsamen Stand.
        </p>
      ) : null}
      <table>
        <caption>Historie · {paket.name}</caption>
        <thead>
          <tr>
            <th scope="col">Bezugswoche</th>
            <th scope="col">Gemeldet am</th>
            <th scope="col">Von</th>
            <th scope="col">Restaufwand</th>
            <th scope="col">Fertig</th>
            <th scope="col">Kommentar</th>
          </tr>
        </thead>
        <tbody>
          {[...meldungen].reverse().map((meldung) => (
            <tr key={meldung.id}>
              <td>{wocheAnzeige(meldung.bezugswoche)}</td>
              <td>{zeitpunktAnzeige(meldung.gemeldetAm)}</td>
              <td>{meldung.gemeldetVon ?? '—'}</td>
              <td>
                {stundenAnzeige(meldung.restHours)}
                {meldung.drift ? <strong> Drift</strong> : null}
                {meldung.widerspruch ? <strong> Widerspruch</strong> : null}
              </td>
              <td>{meldung.fertigstellungsgrad} %</td>
              <td>{meldung.kommentar ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
